// articles/SixesGame.js
// Game guide (Oct 5, 2026): Sixes in TTO, start to finish, for a weekly round.
// Written from the live code: the Sixes card in Games Setup (App.js SixesGamePanel —
// Strokes, Presses), the engine (gameEngines/sixes.js), the Carts card and the
// match-over note on the hole screen (SixesCartsCard, SixesMatchOver), the Press?
// prompt (NassauPressPrompt game="sixes"), the Results tab (SixesMatches) and the
// settlement. Button names are quoted exactly as they appear, so if a label changes in
// App.js, change it here too.
//
// Linked from inside the app: 📖 Guides → Game guides. Public.

import React from 'react';
import { colors, getGuideBySlug } from '../guidesData';
import { H2, H3, P, UL, LI, GuideCTA, FAQ } from '../articleUI';
import { Step, Btn, Heads, StepTracker, PRINT_CSS } from './eventGuideParts';

const guide = getGuideBySlug('how-to-set-up-a-sixes-golf-game');

const FAQS = [
  {
    q: 'Is this the same as Cart, Opposites, Drivers (COD)?',
    a: 'Yes. TTO plays Sixes in the COD order: carts for the first six, opposites (each driver with the other cart’s passenger) for the middle six, drivers against passengers for the last six. Everyone partners everyone once.',
  },
  {
    q: 'We’re walking. How do we set the "carts"?',
    a: 'Use the same Carts card: pair yourselves up and mark one player in each pair as the "driver". The card shows the three matches as you change it, so you can see who partners whom before you tee off.',
  },
  {
    q: 'What happens when a match ends all square?',
    a: 'It’s a push: no money changes hands for that match.',
  },
  {
    q: 'Our group has three players (or five). Can we play Sixes?',
    a: 'No. Sixes needs exactly four in a group, all in the game. A group that isn’t four, or has a player who isn’t in, shows a note in Results and isn’t scored. The other foursomes play on.',
  },
  {
    q: 'A player in our foursome has no handicap. What happens?',
    a: 'On a net Sixes the foursome’s matches are put on hold, because nobody can work out the strokes. The hole screen says so, and the player gets a button to his Profile. Keep scoring: as soon as he adds a Handicap Index, the matches are worked out from every hole already played.',
  },
  {
    q: 'We set the carts wrong. Can we change them?',
    a: 'Yes, until the first score is entered: tap Change on the Carts card. After the first score the carts are locked.',
  },
  {
    q: 'We shotgun-started. Which holes are "1 to 6"?',
    a: 'Your first six holes in the order you play them. A group starting on the 5th plays its first match on 5 to 10, the second on 11 to 16, and the third on 17, 18 and 1 to 4.',
  },
  {
    q: 'Can we play Sixes on nine holes?',
    a: 'No. Sixes is three six-hole matches, so it needs all 18 holes.',
  },
];

export default function SixesGame() {
  return (
    <article>
      <h1
        style={{
          fontFamily: "'DM Serif Display', Georgia, serif",
          fontSize: 38,
          fontWeight: 400,
          color: colors.gray900,
          margin: '0 0 8px',
          lineHeight: 1.15,
        }}
      >
        How to Set Up Sixes
      </h1>
      <style>{PRINT_CSS}</style>
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', margin: '0 0 28px' }}
      >
        <p style={{ fontSize: 15, color: colors.gray400, margin: 0 }}>
          {guide.readMinutes} min read &middot; Game guide
        </p>
        <button
          type="button"
          className="guide-no-print"
          onClick={() => window.print()}
          style={{
            background: 'white',
            color: colors.green800,
            border: `1px solid ${colors.gray300}`,
            borderRadius: 8,
            padding: '8px 14px',
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          🖨️ Print this guide
        </button>
      </div>

      <P>
        Sixes is played <strong>inside each foursome</strong>: three six-hole matches,{' '}
        <strong>two against two</strong>, and everyone partners everyone once. The partners come
        from the carts:
      </P>
      <UL>
        <LI><strong>Holes 1–6:</strong> cart against cart.</LI>
        <LI><strong>Holes 7–12:</strong> each cart&rsquo;s driver with the other cart&rsquo;s passenger.</LI>
        <LI><strong>Holes 13–18:</strong> the two drivers against the two passengers.</LI>
      </UL>
      <P>
        Each hole, each side&rsquo;s better score counts; the lower wins the hole and equal halves
        it. The side that wins more of the six holes wins the match. A tied match is a push.
      </P>

      <StepTracker
        storageKey="tto_guide_sixes_steps"
        steps={['Foursomes', 'Add Sixes', 'Who’s in', 'Carts on the first tee', 'Play', 'Results & settle']}
        note="Steps 1 to 3 happen before the round; 4 to 6 on the day. Tap a step to jump to it."
      />

      <H2>Before you start</H2>
      <UL>
        <LI>
          <strong>Sixes needs live scoring.</strong> Every player needs his scores entered in the app
          as he plays, on his own phone or by the player keeping the foursome&rsquo;s card.
        </LI>
        <LI>
          <strong>Sixes isn&rsquo;t in your games list?</strong> Games come with the Plus plan and
          above. On Free or Starter, upgrade under Settings &rarr; Subscription &amp; Billing.
        </LI>
        <LI>
          <strong>Foursomes, 18 holes.</strong> Each group must be four players, all in the game.
        </LI>
        <LI>
          Decide with your group: <strong>strokes</strong> (net off the low man, net on full course
          handicap, or gross), <strong>presses</strong> (none, automatic or ask the player) and{' '}
          <strong>the stake</strong> per match.
        </LI>
      </UL>

      <H2>Setup, step by step</H2>

      <Step n={1} title="Set the foursomes on the Pairings page" when="Once you know who's playing">
        <UL>
          <LI>
            Build your foursomes on the <strong>Pairings</strong> page and click{' '}
            <Btn>💾 Save Pairings</Btn>. Each foursome plays its own Sixes, and money settles within
            the foursome.
          </LI>
          <LI>
            The order on the pairing is the fallback for the carts: players 1 &amp; 2 in one cart,
            3 &amp; 4 in the other, the first of each driving. The players can change it on the day.
          </LI>
          <LI>
            Just one foursome? No pairings needed: the four players on the tee time are the group.
          </LI>
        </UL>
      </Step>

      <Step n={2} title="Add Sixes" when="Before the round">
        <Heads>
          <strong>On a net game, every player needs a Handicap Index.</strong> A player without one
          can&rsquo;t opt himself in, TTO warns you if you put him in, and his foursome&rsquo;s matches
          wait until he adds one.
        </Heads>
        <UL>
          <LI>
            Click <Btn>🎮 Games</Btn> on the tee time card and tick <strong>Sixes</strong>.
          </LI>
          <LI>
            <strong>Wager:</strong> the amount of <em>each match</em> (and each press). $0 makes it
            results only.
          </LI>
          <LI>
            <strong>Strokes:</strong>
            <UL>
              <LI>
                <Btn>Net — off the low man</Btn>: the low handicap in the foursome plays off scratch
                and the other three get the difference. The default.
              </LI>
              <LI><Btn>Net — full course handicap</Btn>: every player gets all his strokes.</LI>
              <LI><Btn>Gross</Btn>: no strokes.</LI>
            </UL>
          </LI>
          <LI>
            <strong>Presses:</strong>
            <UL>
              <LI><Btn>No presses</Btn>: just the three matches. The default.</LI>
              <LI>
                <Btn>Automatic at 2 down</Btn>: a new bet starts by itself on the next hole when a
                side goes 2 down in a match, and again at 2 down on that press.
              </LI>
              <LI>
                <Btn>Ask the player at 2 down</Btn>: the player whose side goes 2 down sees a{' '}
                <strong>Press?</strong> prompt on his hole screen.
              </LI>
            </UL>
            A press runs to the end of its six-hole match.
          </LI>
          <LI>Click <Btn>Save Games</Btn>.</LI>
        </UL>
      </Step>

      <Step n={3} title="Choose who's in" when="After you save, until the first score">
        <UL>
          <LI>
            In the <strong>Player Opt-Ins</strong> list, click <strong>— Out</strong> to make a player{' '}
            <strong>✓ In</strong>, or let players tap <Btn>Opt In</Btn> in their own app.
          </LI>
          <LI>
            A foursome plays only when <strong>all four</strong> are in. Results shows who is missing
            for a foursome that isn&rsquo;t.
          </LI>
        </UL>
      </Step>

      <H2>On the day</H2>

      <Step n={4} title="Set the carts on the first tee" when="Before the first score">
        <UL>
          <LI>
            Hole 1 shows a <strong>Sixes · carts</strong> card to everyone in the foursome, filled in
            from the pairing order.
          </LI>
          <LI>
            <strong>Swap players:</strong> tap a name, then a name in the other cart.
          </LI>
          <LI>
            <strong>Pick the drivers:</strong> tap the steering wheel next to a name; it turns green.
            The card says <em>&ldquo;✓ Both drivers set&rdquo;</em> when each cart has one.
          </LI>
          <LI>
            The three matches are listed under the carts as you go. Tap <Btn>Done</Btn>: the card
            folds to one line for the whole foursome, with <Btn>Change</Btn> until the first score is
            entered. Then the carts are locked and the card goes away.
          </LI>
          <LI>Anyone in the foursome can set it. Nobody does? The pairing order is used.</LI>
        </UL>
      </Step>

      <Step n={5} title="Play" when="During the round">
        <UL>
          <LI>Each player enters his scores, or one player keeps the foursome&rsquo;s card.</LI>
          <LI>
            <strong>Match over:</strong> when you move on from the 6th and the 12th hole, a note shows
            the result (<em>&ldquo;You &amp; Tom won, 2 up · +$5.00&rdquo;</em>) and your next match
            (<em>&ldquo;Next — holes 7–12: You &amp; Kirk vs Mike &amp; Tom&rdquo;</em>). Close it and
            keep playing. It shows once per match.
          </LI>
          <LI>
            <strong>Pressing</strong> (when presses are set to ask the player): at 2 down a prompt
            above <strong>Next</strong> asks <em>&ldquo;Press?&rdquo;</em>. <Btn>Press — $5</Btn>{' '}
            starts the press to the end of the match; <Btn>Not now</Btn> turns it down, and it comes
            back only if your side falls further behind.
          </LI>
          <LI>
            Follow the matches in <strong>🏆 Leaderboard</strong> &rarr; <strong>💰 Games</strong>:
            your carts, each match (&ldquo;With Kirk vs Mike &amp; Tom&rdquo;), its status and any
            presses.
          </LI>
        </UL>
      </Step>

      <Step n={6} title="Results and settling up" when="When the last card is in">
        <UL>
          <LI>
            The round finalizes itself once every card is complete, or click{' '}
            <Btn>🏁 Finalize Round &amp; Save Settlements</Btn> in <Btn>🎮 Games</Btn>.
          </LI>
          <LI>
            Each match (and press) won: <strong>every winner collects the stake from one player on the
            other side</strong>. A pushed match pays nothing. Money settles within the foursome, one
            payment per pair of players.
          </LI>
          <LI>
            The settlement email shows each player his carts, his three matches and any presses.
          </LI>
        </UL>
        <H3>Example, $5 a match, no presses</H3>
        <P>
          Carts: Al (driving) with Bob, Cal (driving) with Don. Al &amp; Bob win holes 1–6; Al &amp;
          Don win 7–12; 13–18 is a push. Al collects $10 from Cal; Bob and Don are even (Bob won $5
          from Don in the first match and paid $5 to Don in the second).
        </P>
      </Step>

      <FAQ id="sixes-game" faqs={FAQS} />
      <div className="guide-no-print">
        <GuideCTA />
      </div>
    </article>
  );
}
